// src/pages/user/UserManagement.js
// Gestión de usuarios de la aplicación (user.php)

import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Card, Button, Form, Spinner, Table, Modal, InputGroup } from 'react-bootstrap';
import NavContainer from '../../components/Nav';
import OffCanvasNav from '../../components/OffCanvasNav';
import { getUsers, getUserById, createUser, updateUser, deleteUser } from '../../services/userApi';
import { getRoles } from '../../services/roleApi';
import AlertModal from '../../components/AlertModal';
import useAlertModal from '../../hooks/useAlertModal';

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const { showAlert, showAlertModal, alertMessage, alertType, hideAlert, getErrorMessage } = useAlertModal();

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  const [formData, setFormData] = useState({
    firebase_uid: '',
    name: '',
    email: '',
    id_role: '',
    id_person: ''
  });

  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);
      const { ok, status, data } = await getUsers();
      if (ok && (status === 200 || data?.code === 200)) {
        const list = data.data || data.message || [];
        setUsers(Array.isArray(list) ? list : []);
      } else {
        showAlert(getErrorMessage({ data, status }) || 'Error cargando usuarios', 'error');
      }
    } catch (error) {
      console.error('Error cargando usuarios:', error);
      showAlert('Error inesperado al cargar usuarios', 'error');
    } finally {
      setLoading(false);
    }
  }, [showAlert, getErrorMessage]);

  const loadRoles = useCallback(async () => {
    const { ok, data } = await getRoles();
    if (ok && (data?.code === 200 || data?.data)) {
      const list = data.data || data.message || [];
      setRoles(Array.isArray(list) ? list : []);
    }
  }, []);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  useEffect(() => {
    loadRoles();
  }, [loadRoles]);

  const handleOpenCreate = () => {
    setFormData({ firebase_uid: '', name: '', email: '', id_role: '', id_person: '' });
    setSelectedUser(null);
    setShowCreateModal(true);
  };

  const handleOpenEdit = (user) => {
    setSelectedUser(user);
    setFormData({
      firebase_uid: user.firebase_uid || '',
      name: user.name || '',
      email: user.email || '',
      id_role: user.id_role ?? '',
      id_person: user.id_person ?? ''
    });
    setShowEditModal(true);
  };

  const handleOpenDelete = (user) => {
    setSelectedUser(user);
    setShowDeleteModal(true);
  };

  const handleCloseModals = () => {
    setShowCreateModal(false);
    setShowEditModal(false);
    setShowDeleteModal(false);
    setSelectedUser(null);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email) {
      showAlert('Nombre y email son obligatorios', 'error');
      return;
    }
    try {
      setModalLoading(true);
      const payload = {
        name: formData.name,
        email: formData.email,
        firebase_uid: formData.firebase_uid || undefined,
        id_role: formData.id_role ? parseInt(formData.id_role, 10) : undefined,
        id_person: formData.id_person ? parseInt(formData.id_person, 10) : undefined
      };
      const { ok, status, data } = await createUser(payload);
      if (ok && (status === 201 || data?.code === 201)) {
        showAlert(data?.message || 'Usuario creado', 'success');
        handleCloseModals();
        loadUsers();
      } else {
        showAlert(getErrorMessage({ data, status }) || 'Error al crear usuario', 'error');
      }
    } catch (error) {
      console.error('Error creando usuario:', error);
      showAlert('Error inesperado al crear usuario', 'error');
    } finally {
      setModalLoading(false);
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!selectedUser?.id_user) return;
    try {
      setModalLoading(true);
      const payload = {
        name: formData.name,
        email: formData.email,
        firebase_uid: formData.firebase_uid || undefined,
        id_role: formData.id_role ? parseInt(formData.id_role, 10) : undefined,
        id_person: formData.id_person ? parseInt(formData.id_person, 10) : undefined
      };
      const { ok, status, data } = await updateUser(selectedUser.id_user, payload);
      if (ok && status === 200) {
        showAlert(data?.message || 'Usuario actualizado', 'success');
        handleCloseModals();
        loadUsers();
      } else {
        showAlert(getErrorMessage({ data, status }) || 'Error al actualizar usuario', 'error');
      }
    } catch (error) {
      console.error('Error actualizando usuario:', error);
      showAlert('Error inesperado al actualizar usuario', 'error');
    } finally {
      setModalLoading(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!selectedUser?.id_user) return;
    try {
      setModalLoading(true);
      const { ok, status, data } = await deleteUser(selectedUser.id_user);
      if (ok && status === 200) {
        showAlert(data?.message || 'Usuario eliminado', 'success');
        handleCloseModals();
        loadUsers();
      } else {
        showAlert(getErrorMessage({ data, status }) || 'Error al eliminar usuario', 'error');
      }
    } catch (error) {
      console.error('Error eliminando usuario:', error);
      showAlert('Error inesperado al eliminar usuario', 'error');
    } finally {
      setModalLoading(false);
    }
  };

  return (
    <>
      <NavContainer />
      <OffCanvasNav />
      <Container fluid className="mt-3">
        <Row>
          <Col>
            <Card className="shadow-sm">
              <Card.Header className="d-flex justify-content-between align-items-center">
                <h5 className="mb-0">
                  <i className="fas fa-users me-2"></i>
                  Gestión de usuarios
                </h5>
                <Button variant="primary" onClick={handleOpenCreate}>
                  <i className="fas fa-plus me-1"></i>
                  Nuevo usuario
                </Button>
              </Card.Header>
              <Card.Body>
                {loading ? (
                  <div className="d-flex justify-content-center py-5">
                    <Spinner animation="border" />
                  </div>
                ) : (
                  <Table responsive striped bordered hover>
                    <thead>
                      <tr>
                        <th>ID</th>
                        <th>Nombre</th>
                        <th>Email</th>
                        <th>Rol</th>
                        <th>Tipo persona</th>
                        <th>Empleado / Rol empleado</th>
                        <th className="text-center">Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.length === 0 ? (
                        <tr>
                          <td colSpan="7" className="text-center text-muted py-4">
                            No hay usuarios registrados
                          </td>
                        </tr>
                      ) : (
                        users.map((u) => (
                          <tr key={u.id_user}>
                            <td>{u.id_user}</td>
                            <td>{u.name || '-'}</td>
                            <td>{u.email || '-'}</td>
                            <td>{u.role_name || u.id_role || '-'}</td>
                            <td>{u.person_type_name || u.person_type || '-'}</td>
                            <td>
                              {u.employee_role_name ? (
                                <span>{u.person_full_name || u.groomer_name} ({u.employee_role_name})</span>
                              ) : (
                                '-'
                              )}
                            </td>
                            <td className="text-center">
                              <Button variant="outline-primary" size="sm" className="me-1" onClick={() => handleOpenEdit(u)}>
                                <i className="fas fa-edit"></i>
                              </Button>
                              <Button variant="outline-danger" size="sm" onClick={() => handleOpenDelete(u)}>
                                <i className="fas fa-trash"></i>
                              </Button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </Table>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Modal show={showCreateModal} onHide={handleCloseModals}>
          <Modal.Header closeButton>
            <Modal.Title>Nuevo usuario</Modal.Title>
          </Modal.Header>
          <Form onSubmit={handleCreate}>
            <Modal.Body>
              <Form.Group className="mb-3">
                <Form.Label>Nombre *</Form.Label>
                <Form.Control name="name" value={formData.name} onChange={handleChange} required />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Email *</Form.Label>
                <Form.Control type="email" name="email" value={formData.email} onChange={handleChange} required />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Firebase UID (opcional)</Form.Label>
                <Form.Control name="firebase_uid" value={formData.firebase_uid} onChange={handleChange} placeholder="Se asigna al registrarse" />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Rol (opcional)</Form.Label>
                <Form.Select name="id_role" value={formData.id_role} onChange={handleChange}>
                  <option value="">Sin asignar</option>
                  {roles.map((r) => (
                    <option key={r.id_role} value={r.id_role}>
                      {r.role_name || `Rol ${r.id_role}`}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>ID Persona (opcional)</Form.Label>
                <Form.Control type="number" name="id_person" value={formData.id_person} onChange={handleChange} placeholder="Vincular persona" />
              </Form.Group>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={handleCloseModals}>Cancelar</Button>
              <Button variant="primary" type="submit" disabled={modalLoading}>
                {modalLoading ? <Spinner animation="border" size="sm" /> : 'Crear'}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>

        <Modal show={showEditModal} onHide={handleCloseModals}>
          <Modal.Header closeButton>
            <Modal.Title>Editar usuario</Modal.Title>
          </Modal.Header>
          <Form onSubmit={handleUpdate}>
            <Modal.Body>
              <Form.Group className="mb-3">
                <Form.Label>Nombre *</Form.Label>
                <Form.Control name="name" value={formData.name} onChange={handleChange} required />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Email *</Form.Label>
                <Form.Control type="email" name="email" value={formData.email} onChange={handleChange} required />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Firebase UID (opcional)</Form.Label>
                <Form.Control name="firebase_uid" value={formData.firebase_uid} onChange={handleChange} />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Rol (opcional)</Form.Label>
                <Form.Select name="id_role" value={formData.id_role} onChange={handleChange}>
                  <option value="">Sin asignar</option>
                  {roles.map((r) => (
                    <option key={r.id_role} value={r.id_role}>
                      {r.role_name || `Rol ${r.id_role}`}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>ID Persona (opcional)</Form.Label>
                <Form.Control type="number" name="id_person" value={formData.id_person} onChange={handleChange} />
              </Form.Group>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={handleCloseModals}>Cancelar</Button>
              <Button variant="primary" type="submit" disabled={modalLoading}>
                {modalLoading ? <Spinner animation="border" size="sm" /> : 'Guardar'}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>

        <Modal show={showDeleteModal} onHide={handleCloseModals}>
          <Modal.Header closeButton>
            <Modal.Title>Eliminar usuario</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            ¿Eliminar al usuario <strong>{selectedUser?.name}</strong> ({selectedUser?.email})? Esta acción no se puede deshacer.
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseModals}>Cancelar</Button>
            <Button variant="danger" onClick={handleConfirmDelete} disabled={modalLoading}>
              {modalLoading ? <Spinner animation="border" size="sm" /> : 'Eliminar'}
            </Button>
          </Modal.Footer>
        </Modal>

        <AlertModal show={showAlertModal} onHide={hideAlert} message={alertMessage} type={alertType} />
      </Container>
    </>
  );
};

export default UserManagement;
